import React, { useState, useRef, MouseEvent, ButtonHTMLAttributes } from 'react';
import styles from './index.module.scss'; // 确保路径正确

// 定义 Ripple 接口
interface Ripple {
  id: number;
  x: number;
  y: number;
  size: number;
}

// 定义 RippleButton 的 Props
// 继承 ButtonHTMLAttributes<HTMLButtonElement> 使得组件可以接收所有标准的 <button> 属性，
// 例如 type, disabled, name, form 等。
interface RippleButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode; // 按钮内容
  duration?: number; // 波纹动画持续时间，默认为 600ms
  rippleColor?: string; // 波纹颜色，默认为 rgba(255, 255, 255, 0.3)
  // 你之前传递的 buttonClass 可以直接作为 className 属性来接收
  // buttonClass: string; // 这个不再需要单独定义，因为 ButtonHTMLAttributes 包含了 className
}

const RippleButton: React.FC<RippleButtonProps> = ({
  children,
  duration = 600,
  rippleColor = 'rgba(255, 255, 255, 0.3)',
  onClick, // 捕获原始的 onClick
  className, // 捕获原始的 className
  ...rest // 捕获所有其他标准的 button 属性
}) => {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null); // 用于获取按钮尺寸和位置

  const handleRippleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!buttonRef.current) return;

    const button = buttonRef.current;
    const rect = button.getBoundingClientRect();
    // 确保波纹大小足够大，能够覆盖整个按钮
    const size = Math.max(rect.width, rect.height) * 2;
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    const newRipple: Ripple = {
      id: Date.now(),
      x,
      y,
      size,
    };

    setRipples((prevRipples) => [...prevRipples, newRipple]);

    // 动画结束后移除波纹
    setTimeout(() => {
      setRipples((prevRipples) => prevRipples.filter((ripple) => ripple.id !== newRipple.id));
    }, duration);

    // 调用原始的 onClick 事件，如果它存在
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      ref={buttonRef}
      className={`${styles.ctaButton} ${className || ''}`} // 组合你的 SCSS 样式和外部传入的 className
      onClick={handleRippleClick}
      {...rest} // 展开所有剩余的标准 button 属性
    >
      {children} {/* 渲染按钮的子元素，例如文本 */}
      {ripples.map((ripple) => (
        <div
          key={ripple.id}
          className={styles.ripple} // 应用 SCSS 中定义的 .ripple 样式
          style={{
            left: ripple.x,
            top: ripple.y,
            width: ripple.size,
            height: ripple.size,
            backgroundColor: rippleColor, // 使用 props 传入的波纹颜色
            animationDuration: `${duration}ms`, // 使用 props 传入的持续时间
          }}
        />
      ))}
    </button>
  );
};

export default RippleButton;